import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Loader2, ExternalLink, Info, Phone, CheckCircle2, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { API_CONFIG } from '@/lib/api/config';
import { ApiError } from '@/lib/api';
import { friendlyMessage, showApiError } from '@/lib/errors';
import { resolveFbSdkVersion } from '@/lib/meta/graphApiVersion';
import { useQueryClient } from '@tanstack/react-query';
import { useWorkspaceContext } from '@/contexts/WorkspaceContext';

declare global {
  interface Window {
    FB?: {
      init: (options: Record<string, unknown>) => void;
      login: (
        callback: (response: any) => void,
        options?: Record<string, unknown>
      ) => void;
    };
    fbAsyncInit?: () => void;
    __fbSdkInitialized?: boolean;
  }
}

type EmbeddedSignupConfig = {
  appId: string;
  configId: string;
  redirectUri: string;
  graphApiVersion: string;
  solutionId?: string | null;
};

type FlowStatus = 'idle' | 'launching' | 'waiting' | 'saving' | 'selecting_phone';

interface PhoneNumber {
  id: string;
  display_phone_number: string;
  verified_name?: string;
  quality_rating?: string;
  messaging_limit_tier?: string;
  code_verification_status?: string;
  is_connected?: boolean;
}

interface WhatsAppEmbeddedSignupProps {
  onSuccess?: (businessAccounts: any[]) => void;
  onError?: (message: string) => void;
  existingAccountId?: string;
  autoActivateOnboarding?: boolean;
  autoLaunch?: boolean;
   startLabel?: string;
  startIcon?: ReactNode;
  showChecklist?: boolean;
  showSecurityNote?: boolean;
}

export type ConnectionType = 'coexistence' | 'direct';

export function resolveConnectionType(eventName: unknown): ConnectionType | undefined {
  if (typeof eventName !== 'string') return undefined;
  const normalized = eventName.toUpperCase();
  if (normalized === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') return 'coexistence';
  if (normalized === 'FINISH') return 'direct';
  return undefined;
}

const FACEBOOK_SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js';
const FACEBOOK_ORIGINS = [
  'https://www.facebook.com',
  'https://web.facebook.com',
  'https://business.facebook.com',
];

export function WhatsAppEmbeddedSignup({
  onSuccess,
  onError,
  existingAccountId,
  autoActivateOnboarding = true,
  autoLaunch = false,
  startLabel = 'Connect WhatsApp',
  startIcon,
  showChecklist = true,
  showSecurityNote = true,
}: WhatsAppEmbeddedSignupProps) {
  const { selectedWorkspaceId } = useWorkspaceContext();
  const queryClient = useQueryClient();
  const [config, setConfig] = useState<EmbeddedSignupConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [flowStatus, setFlowStatus] = useState<FlowStatus>('idle');
  const [lastEvent, setLastEvent] = useState<string | null>(null);
  const sdkLoaderRef = useRef<Promise<typeof window.FB | undefined>>();
  const accessTokenRef = useRef<string | null>(null); // Store access token from FB.login
  const tokenExchangePromiseRef = useRef<Promise<string | null> | null>(null); // Store token exchange promise
  const autoLaunchedRef = useRef(false); // Guard so auto-launch fires only once
  const flowStatusRef = useRef<FlowStatus>('idle');
  flowStatusRef.current = flowStatus;

  const [availablePhoneNumbers, setAvailablePhoneNumbers] = useState<PhoneNumber[]>([]);
  const [selectedPhoneNumberId, setSelectedPhoneNumberId] = useState<string | null>(null);
  const [showPhoneNumberSelector, setShowPhoneNumberSelector] = useState(false);
  const [showManualFallback, setShowManualFallback] = useState(false);
  const [fetchingWABA, setFetchingWABA] = useState(false);
  const [normalizedPayload, setNormalizedPayload] = useState<any>(null);
  const [pendingAccessToken, setPendingAccessToken] = useState<string | null>(null);
  const [pendingConnectionType, setPendingConnectionType] = useState<ConnectionType | undefined>(
    undefined
  );

  const authToken = useMemo(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    return localStorage.getItem('auth_token');
  }, []);

  const resetOpenFlow = useCallback(() => {
    const status = flowStatusRef.current;
    if (status === 'saving' || status === 'selecting_phone') return;
    setFlowStatus('idle');
    accessTokenRef.current = null;
    tokenExchangePromiseRef.current = null;
    setShowManualFallback(false);
  }, []);

  const fetchConfig = useCallback(async () => {
    if (!authToken) {
      setConfigError('You must be signed in to start WhatsApp embedded signup.');
      setIsLoadingConfig(false);
      return;
    }

    try {
      setIsLoadingConfig(true);
      const response = await fetch(`${API_CONFIG.BASE_URL}/meta/embedded-signup/config`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw ApiError.fromResult(errorData, 'Failed to load embedded signup configuration.');
      }

      const data = await response.json();
      setConfig(data.config);
      setConfigError(null);
    } catch (error: any) {
      const message = friendlyMessage(error, "We couldn't load WhatsApp setup. Please try again.");
      setConfigError(message);
      onError?.(message);
    } finally {
      setIsLoadingConfig(false);
    }
  }, [authToken, onError]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const initFacebookSdk = useCallback(
    () =>
      new Promise<typeof window.FB | undefined>((resolve, reject) => {
        if (!config?.appId) {
          reject(new Error('Missing Meta App ID for embedded signup.'));
          return;
        }

        const initializeSdk = () => {
          if (!window.FB) {
            reject(new Error('Facebook SDK unavailable after load.'));
            return;
          }

          if (!window.__fbSdkInitialized) {
            window.FB.init({
              appId: config.appId,
              autoLogAppEvents: false,
              cookie: true,
              xfbml: false,
              version: resolveFbSdkVersion(config.graphApiVersion),
            });
            window.__fbSdkInitialized = true;
          }
          resolve(window.FB);
        };

        if (window.FB) {
          initializeSdk();
          return;
        }

        window.fbAsyncInit = initializeSdk;

        if (document.getElementById('facebook-jssdk')) {
          return;
        }

        const script = document.createElement('script');
        script.id = 'facebook-jssdk';
        script.src = FACEBOOK_SDK_SRC;
        script.async = true;
        script.defer = true;
        script.onerror = () => reject(new Error('Failed to load Facebook SDK.'));
        document.body.appendChild(script);
      }),
    [config]
  );

  const ensureFacebookSdk = useCallback(async () => {
    if (!sdkLoaderRef.current) {
      sdkLoaderRef.current = initFacebookSdk();
    }

    return sdkLoaderRef.current;
  }, [initFacebookSdk]);

  const normalizeEmbeddedPayload = useCallback((rawPayload: any) => {
    if (!rawPayload) {
      return null;
    }

    let payload = rawPayload;
    if (typeof payload === 'string') {
      try {
        payload = JSON.parse(payload);
      } catch (error) {
        return null;
      }
    }

    if (payload?.data && typeof payload.data === 'object') {
      payload = payload.data;
    }

    console.log('[EmbeddedSignup] normalizeEmbeddedPayload raw:', JSON.stringify(payload, null, 2));

    const wabaId =
      payload.waba_id ||
      payload.wabaId ||
      payload.phone_waba_id ||
      payload.whatsapp_business_account_id ||
      payload.whatsappBusinessAccountId ||
      payload.whatsapp_business_account?.id ||
      payload.waba?.id ||
      payload.phone?.waba_id ||
      payload.id; // Sometimes the ID is directly in the root

    const accessToken =
      payload.access_token ||
      payload.accessToken ||
      payload.long_lived_token ||
      payload.longLivedToken ||
      payload.token ||
      payload.user_access_token ||
      accessTokenRef.current; // Use the token from FB.login authResponse

    const phoneNumberId =
      payload.phone_number_id ||
      payload.phoneNumberId ||
      payload.phone_number?.id ||
      payload.phoneNumber?.id;

    const phoneNumber =
      payload.phone_number ||
      payload.display_phone_number ||
      payload.phoneNumber ||
      payload.phone_number?.display_phone_number;

    const businessId =
      payload.business_id ||
      payload.businessId ||
      payload.business?.id;

    const businessName =
      payload.business_name ||
      payload.businessName ||
      payload.whatsapp_business_account?.name ||
      payload.name ||
      (wabaId ? `WhatsApp Business Account ${wabaId}` : null);


    return {
      raw: payload,
      wabaId,
      businessId,
      accessToken,
      phoneNumber,
      phoneNumberId,
      businessName,
    };
  }, []);

  const completeEmbeddedSignup = useCallback(
    async (payload: any, connectionType?: ConnectionType) => {
      try {

        const normalized = normalizeEmbeddedPayload(payload);

        let accessToken = normalized?.accessToken || accessTokenRef.current;
        let tokenExchangeError: string | null = null;

        if (!accessToken && tokenExchangePromiseRef.current) {
          try {
            accessToken = await tokenExchangePromiseRef.current;
          } catch (err: any) {
            tokenExchangeError = err?.message || 'Token exchange failed';
            accessToken = null;
          }
        } else if (!accessToken && !tokenExchangePromiseRef.current) {
          let waitCount = 0;
          while (!accessToken && !tokenExchangePromiseRef.current && waitCount < 30) {
            await new Promise(resolve => setTimeout(resolve, 100));
            accessToken = accessTokenRef.current;
            waitCount++;
          }
          if (tokenExchangePromiseRef.current && !accessToken) {
            try {
              accessToken = await tokenExchangePromiseRef.current;
            } catch (err: any) {
              tokenExchangeError = err?.message || 'Token exchange failed';
            }
          }
        }

        if (!normalized?.wabaId) {
          throw new Error('Meta did not return a WhatsApp Business account. Please try again.');
        }

        if (!accessToken) {
          throw new Error(tokenExchangeError || 'Could not obtain an access token from Meta. Check that your Meta App is Live and the OAuth redirect URI is configured correctly.');
        }

        if (!authToken) {
          throw new Error('Authentication token missing. Please sign in again.');
        }

        setNormalizedPayload(normalized);
        setPendingAccessToken(accessToken);
        setPendingConnectionType(connectionType);

        setFlowStatus('selecting_phone');

        try {
          const phoneNumbersResponse = await fetch(
            `${API_CONFIG.BASE_URL}/meta/waba/${normalized.wabaId}/phone-numbers?access_token=${encodeURIComponent(accessToken)}`,
            {
              headers: {
                Authorization: `Bearer ${authToken}`,
              },
            }
          );

          if (!phoneNumbersResponse.ok) {
            await createBusinessAccountWithPhone(
              normalized,
              accessToken,
              normalized.phoneNumberId,
              connectionType
            );
            return;
          }

          const phoneData = await phoneNumbersResponse.json();
          const availablePhones = phoneData.data || [];


          if (availablePhones.length === 0) {
            throw new Error('No phone numbers found for this WhatsApp Business Account');
          }

          if (availablePhones.length === 1) {
            await createBusinessAccountWithPhone(
              normalized,
              accessToken,
              availablePhones[0].id,
              connectionType
            );
          } else {
            setAvailablePhoneNumbers(availablePhones);
            setShowPhoneNumberSelector(true);
            setFlowStatus('idle');
          }
        } catch (fetchError: any) {
          await createBusinessAccountWithPhone(
            normalized,
            accessToken,
            normalized.phoneNumberId,
            connectionType
          );
        }
      } catch (error: any) {
        setFlowStatus('idle');

        accessTokenRef.current = null;
        tokenExchangePromiseRef.current = null;

        const message = friendlyMessage(error, "We couldn't save your WhatsApp account. Please try again.");
        setConfigError(message);
        onError?.(message);
        showApiError(error, { fallback: { title: 'Embedded signup failed' } });
      }
    },
    [authToken, normalizeEmbeddedPayload, onError, onSuccess]
  );

  const createBusinessAccountWithPhone = useCallback(
    async (
      normalized: any,
      accessToken: string,
      phoneNumberId?: string,
      connectionType?: ConnectionType
    ) => {
      try {
        if (!authToken) {
          throw new Error('Authentication token missing. Please sign in again.');
        }

        setFlowStatus('saving');

        const businessAccountPayload = {
          id: normalized.wabaId,
          business_id: normalized.businessId,
          name: normalized.businessName || `WhatsApp Business Account ${normalized.wabaId}`,
          phone_number_id: phoneNumberId || normalized.phoneNumberId, // Use selected phone number ID
        };


        const response = await fetch(`${API_CONFIG.BASE_URL}/meta/create-business-account`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            business_account: businessAccountPayload,
            access_token: accessToken, // Use token from payload or ref
            ...(connectionType ? { connection_type: connectionType } : {}),
            ...(existingAccountId && { existing_account_id: existingAccountId }),
            ...(selectedWorkspaceId ? { workspace_id: selectedWorkspaceId } : {}),
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw ApiError.fromResult(errorData, 'Failed to store WhatsApp account.');
        }

        const data = await response.json();
        setFlowStatus('idle');
        setLastEvent('finish');

        accessTokenRef.current = null;
        tokenExchangePromiseRef.current = null;
        setShowPhoneNumberSelector(false);
        setAvailablePhoneNumbers([]);
        setSelectedPhoneNumberId(null);
        setNormalizedPayload(null);
        setPendingAccessToken(null);
        setPendingConnectionType(undefined);

        const newAccountId = data.businessAccount?.id;
        if (autoActivateOnboarding && newAccountId) {
          await fetch(`${API_CONFIG.BASE_URL}/onboarding/activate`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({ business_account_id: newAccountId }),
          }).catch(() => { /* non-fatal */ });
        }

        toast({ variant: "success",
          title: 'WhatsApp account connected',
          description: 'Your number is now ready to use with PhotonX.',
        });
        queryClient.invalidateQueries({ queryKey: ['workspaces-v1'] });
        if (data.businessAccount) {
          onSuccess?.([data.businessAccount]);
        } else {
          onSuccess?.([]);
        }
      } catch (error: any) {
        setFlowStatus('idle');

        accessTokenRef.current = null;
        tokenExchangePromiseRef.current = null;

        const message = friendlyMessage(error, "We couldn't save your WhatsApp account. Please try again.");
        setConfigError(message);
        onError?.(message);
        showApiError(error, { fallback: { title: 'Failed to connect account' } });
      }
    },
    [authToken, autoActivateOnboarding, existingAccountId, onError, onSuccess, selectedWorkspaceId, queryClient]
  );

  const handleSignupMessage = useCallback(
    (event: MessageEvent) => {
      if (!FACEBOOK_ORIGINS.includes(event.origin)) {
        return;
      }

      let payload = event.data;
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload);
        } catch (error) {
          return;
        }
      }

      if (!payload || payload.type !== 'WA_EMBEDDED_SIGNUP') {
        return;
      }


      setLastEvent(payload.event);

      const eventName =
        typeof payload.event === 'string' ? payload.event.toUpperCase() : '';

      if (eventName === 'FINISH' || eventName === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
        completeEmbeddedSignup(payload.data, resolveConnectionType(eventName));
      } else if (eventName === 'CANCEL' || eventName === 'ABORT') {
        resetOpenFlow();
        toast({
          variant: 'success',
          title: 'Signup cancelled',
          description: 'The Meta window was closed before completion.',
        });
      } else if (eventName === 'ERROR') {
        resetOpenFlow();
        const message = friendlyMessage(payload.errorMessage, "Facebook couldn't finish the setup. Please try again.");
        setConfigError(message);
        onError?.(message);
        toast({
          title: 'Embedded signup error',
          description: message,
          variant: 'destructive',
        });
      }
    },
    [completeEmbeddedSignup, onError, resetOpenFlow]
  );

  useEffect(() => {
    window.addEventListener('message', handleSignupMessage);
    return () => window.removeEventListener('message', handleSignupMessage);
  }, [handleSignupMessage]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const recoverIfMetaAbandoned = () => {
      window.clearTimeout(timer);
      timer = setTimeout(() => {
        const status = flowStatusRef.current;
        if (status === 'launching') {
          resetOpenFlow();
          return;
        }
        if (
          status === 'waiting' &&
          !accessTokenRef.current &&
          !tokenExchangePromiseRef.current
        ) {
          resetOpenFlow();
        }
      }, 500);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') recoverIfMetaAbandoned();
    };
    window.addEventListener('focus', recoverIfMetaAbandoned);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('focus', recoverIfMetaAbandoned);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [resetOpenFlow]);

  useEffect(() => {
    if (flowStatus !== 'launching') return;
    const timer = window.setTimeout(() => {
      if (flowStatusRef.current === 'launching') {
        resetOpenFlow();
        toast({
          title: 'Meta setup did not open',
          description: 'Please try again. Allow pop-ups for this site if your browser blocked them.',
          variant: 'destructive',
        });
      }
    }, 15_000);
    return () => window.clearTimeout(timer);
  }, [flowStatus, resetOpenFlow]);

  const launchEmbeddedSignup = useCallback(async () => {
    if (!config) {
      return;
    }

    try {
      setConfigError(null);
      setFlowStatus('launching');
      const FB = await ensureFacebookSdk();

      if (!FB) {
        throw new Error('Facebook SDK is unavailable. Please refresh and try again.');
      }

      FB.login(
        (response: any) => {
          if (response?.authResponse) {
            const code = response.authResponse.code;

            if (code) {
              setFlowStatus('waiting');

              const tokenExchangePromise = (async (): Promise<string | null> => {
                try {
                  const requestPayload = {
                    code: code,
                  };

                  const tokenResponse = await fetch(`${API_CONFIG.BASE_URL}/meta/exchange-token`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(requestPayload),
                  });

                  if (!tokenResponse.ok) {
                    const errorData = await tokenResponse.json().catch(() => ({}));
                    throw ApiError.fromResult(errorData, 'Failed to exchange authorization code for access token');
                  }

                  const tokenData = await tokenResponse.json();

                  const accessToken = tokenData.data?.access_token || tokenData.data?.accessToken || tokenData.access_token || tokenData.accessToken;

                  if (accessToken) {
                    accessTokenRef.current = accessToken;
                    return accessToken;
                  } else {
                    console.error('Token exchange response:', tokenData);
                    throw new Error('Token exchange succeeded but no access token in response');
                  }
                } catch (error: any) {
                  resetOpenFlow();
                  const message = friendlyMessage(error, "We couldn't finish the Facebook sign-in. Please try again.");
                  setConfigError(message);
                  onError?.(message);
                  showApiError(error, { fallback: { title: 'Token exchange failed' } });
                  return null;
                }
              })();

              tokenExchangePromiseRef.current = tokenExchangePromise;

              tokenExchangePromise.then(async (token) => {
                if (!token) return;
                await new Promise(r => setTimeout(r, 4000));
                if (!accessTokenRef.current) return;
                setShowManualFallback(true);
              }).catch(() => {});
            } else {
              resetOpenFlow();
            }
          } else {
            resetOpenFlow();
            setConfigError(null);
          }
        },
        {
          config_id: config.configId,
          response_type: 'code',
          override_default_response_type: true,
          extras: config.solutionId
            ? {
              setup: { solutionID: config.solutionId },
              featureType: "whatsapp_business_app_onboarding",
              sessionInfoVersion: '3',
            }
            : {
              featureType: "whatsapp_business_app_onboarding",
              sessionInfoVersion: '3',
            },
        }
      );

      if (flowStatusRef.current === 'launching') {
        setFlowStatus('waiting');
      }
    } catch (error: any) {
      resetOpenFlow();
      const message = friendlyMessage(error, "We couldn't open WhatsApp setup. Please try again.");
      setConfigError(message);
      onError?.(message);
      showApiError(error, { fallback: { title: 'Embedded signup unavailable' } });
    }
  }, [config, ensureFacebookSdk, onError, resetOpenFlow]);

  useEffect(() => {
    if (
      autoLaunch &&
      config &&
      !autoLaunchedRef.current &&
      flowStatus === 'idle'
    ) {
      autoLaunchedRef.current = true;
      launchEmbeddedSignup();
    }
  }, [autoLaunch, config, flowStatus, launchEmbeddedSignup]);

  const fetchWABAManually = useCallback(async () => {
    const token = accessTokenRef.current;
    if (!token || !authToken) return;
    try {
      setFetchingWABA(true);
      const response = await fetch(`${API_CONFIG.BASE_URL}/meta/fetch-waba-from-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ access_token: token }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw ApiError.fromResult(data, 'Failed to fetch WhatsApp accounts from Meta');
      }
      const wabas: { wabaId: string; businessId: string; businessName: string; wabaName: string; phoneNumbers: { id: string; display_phone_number: string }[] }[] = data.data || [];
      if (wabas.length === 0) {
        throw new Error('No WhatsApp Business Accounts found for this login. Make sure your account has a WABA.');
      }
      const first = wabas[0];
      const normalized = {
        wabaId: first.wabaId,
        businessId: first.businessId,
        businessName: first.businessName || first.wabaName,
        accessToken: token,
        phoneNumberId: first.phoneNumbers?.[0]?.id,
        phoneNumber: first.phoneNumbers?.[0]?.display_phone_number,
        raw: first,
      };
      setShowManualFallback(false);
      if (first.phoneNumbers?.length > 1) {
        setAvailablePhoneNumbers(first.phoneNumbers);
        setNormalizedPayload(normalized);
        setPendingAccessToken(token);
        setPendingConnectionType(undefined);
        setShowPhoneNumberSelector(true);
        setFlowStatus('idle');
      } else {
        await createBusinessAccountWithPhone(normalized, token, normalized.phoneNumberId);
      }
    } catch (err: unknown) {
      showApiError(err, { fallback: { title: 'Could not fetch account' } });
    } finally {
      setFetchingWABA(false);
    }
  }, [authToken, createBusinessAccountWithPhone]);

  const isButtonDisabled =
    isLoadingConfig || !config || flowStatus === 'launching' || flowStatus === 'saving';

  return (
    <div className="space-y-6">
      {configError && (
        <Alert variant="destructive">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{configError}</AlertDescription>
        </Alert>
      )}

      {!autoLaunch && showChecklist && (
        <div className="rounded-lg border bg-muted/40 p-4 space-y-3">
          <p className="text-sm font-medium">Before you begin, make sure you have:</p>
          <div className="space-y-2.5">
            {[
              'Your Meta Business account login credentials.',
              'Admin access to your WhatsApp Business account.',
              'A phone number ready to receive a verification code via SMS or voice call.',
            ].map((item) => (
              <div key={item} className="flex items-start gap-2.5">
                <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-muted-foreground">{item}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Button
        onClick={launchEmbeddedSignup}
        disabled={isButtonDisabled}
        className="w-full"
        size="lg"
      >
        {flowStatus === 'launching' || isLoadingConfig ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Opening Meta setup...
          </>
        ) : flowStatus === 'waiting' ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Waiting for Meta window...
          </>
        ) : flowStatus === 'saving' ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Connecting your account...
          </>
        ) : (
          <>
            {startIcon ?? <ExternalLink className="h-4 w-4 mr-2" />}
            {startLabel}
          </>
        )}
      </Button>

      {!autoLaunch && showSecurityNote && (
        <div className="flex items-start gap-2 text-xs text-muted-foreground">
          <ShieldCheck className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-primary/60" />
          <span>
            A secure window from Meta will open to complete the setup. Your account will be linked to PhotonX automatically — no manual steps required.
          </span>
        </div>
      )}

      {showManualFallback && flowStatus === 'waiting' && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>Finished setting up on your phone?</AlertTitle>
          <AlertDescription className="space-y-3 mt-2">
            <p className="text-sm text-muted-foreground">
              If you've completed all the steps in the Meta window and paired your WhatsApp Business app, click below to finalize the connection.
            </p>
            <Button
              size="sm"
              onClick={fetchWABAManually}
              disabled={fetchingWABA}
              className="w-full"
            >
              {fetchingWABA ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Connecting your account...</>
              ) : (
                'Finalize Connection'
              )}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {showPhoneNumberSelector && availablePhoneNumbers.length > 0 && (
        <Alert>
          <Phone className="h-4 w-4" />
          <AlertTitle>Select a phone number to connect</AlertTitle>
          <AlertDescription className="space-y-4 mt-3">
            <p className="text-sm text-muted-foreground">
              {availablePhoneNumbers.length} numbers were found on this account. Choose which one to connect:
            </p>

            <div className="space-y-2">
              {availablePhoneNumbers.map((phone) => (
                <Button
                  key={phone.id}
                  variant={selectedPhoneNumberId === phone.id ? "default" : "outline"}
                  className="w-full justify-start h-auto py-3"
                  onClick={() => setSelectedPhoneNumberId(phone.id)}
                  disabled={phone.is_connected}
                >
                  <div className="flex items-center gap-3 w-full">
                    <Phone className="h-4 w-4 flex-shrink-0" />
                    <div className="flex-1 text-left min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">
                          {phone.display_phone_number || phone.id}
                        </span>
                        {phone.is_connected && (
                          <Badge variant="secondary" className="text-xs">
                            Already Connected
                          </Badge>
                        )}
                      </div>
                      {phone.verified_name && (
                        <p className="text-xs text-muted-foreground truncate mt-1">
                          {phone.verified_name}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {phone.quality_rating && (
                        <Badge
                          variant="outline"
                          className={`text-xs ${phone.quality_rating === 'GREEN' ? 'border-green-500 text-green-700 dark:text-green-400' :
                            phone.quality_rating === 'YELLOW' ? 'border-yellow-500 text-yellow-700 dark:text-yellow-400' :
                              'border-red-500 text-red-700 dark:text-red-400'
                            }`}
                        >
                          {phone.quality_rating}
                        </Badge>
                      )}
                      {phone.messaging_limit_tier && (
                        <Badge variant="outline" className="text-xs">
                          {phone.messaging_limit_tier}
                        </Badge>
                      )}
                    </div>
                  </div>
                </Button>
              ))}
            </div>

            <Button
              onClick={() => {
                if (selectedPhoneNumberId && normalizedPayload && pendingAccessToken) {
                  createBusinessAccountWithPhone(
                    normalizedPayload,
                    pendingAccessToken,
                    selectedPhoneNumberId,
                    pendingConnectionType
                  );
                }
              }}
              disabled={!selectedPhoneNumberId || flowStatus === 'saving'}
              className="w-full"
            >
              {flowStatus === 'saving' ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Connecting...
                </>
              ) : (
                'Connect This Number'
              )}
            </Button>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
